// Copyright (c) 2026 Endel Dreyer.
//
// This software is released under the MIT License.
// https://opensource.org/license/MIT
//
// colyseus.js@0.18.2
import { encode } from '@colyseus/schema';
import { Protocol, ProtocolModifier } from '@colyseus/shared-types';
import { now } from '../core/utils.mjs';
import { debugOverlayActive } from '../debug-channel.mjs';
import { metadataOf } from '../core/schema-reflect.mjs';

/** Widest a single self-describing number can encode to. */
const MAX_VARINT = 9;
/** Reliable stamp prefix: one delta-coded timeline value (+2 for BOTH's u16). */
const RELIABLE_STAMP_MAX = MAX_VARINT;
/** Unreliable stamp block: `[k][u32 anchor][Δ]×(k−1)`, twice over for BOTH. */
const ringStampMax = (historySize, both) => (both ? 2 : 1) * (MAX_VARINT + 4 + MAX_VARINT * historySize);
/** @internal */
class InputHandleImpl {
    data;
    _host;
    _encoder;
    // Reused frame buffer — safe ONLY because every transport copies synchronously
    // on send (browser ws.send snapshots; H3 frame() copies before write). A
    // transport that queued the view uncopied would see it overwritten next send.
    _scratch = new Uint8Array(2048);
    // Cached framed-packet view into `_scratch`; re-made when the packet size
    // changes (delta bodies vary) or `_scratch` grows — so it mostly pays off on
    // steady no-change frames.
    _framed = null;
    // Input round-trip state (one handle per room).
    _sentCount = 0; // reliable inputs transmitted
    _lastProcessed = 0; // server-acked (consumedCount)
    _epoch = 0; // reset counter — see InputHandle.epoch
    // RTT send-time ring (seq % size → send time); avoids per-send Map churn.
    // Sized WITH the replay ring (one seq window — replay and RTT age out together).
    _sendTimes;
    // Sent-input replay ring, mirroring the server's per-client input buffer: each reliable send snapshots
    // `data` into slot `seq % size` via alloc-free `copyInto` so a reconciler can replay unacked inputs.
    // Size = worst-case in-flight = (RTT + patch interval) × input rate. tickRate/patchRate from the
    // handshake (ack rides the patch, so lags up to one interval); RTT is budgeted generously. Floored
    // at 64; grows for high input rates where 64 would silently overflow (aged-out entries warn once).
    static BUFFER_FLOOR = 64;
    static BUFFER_RTT_BUDGET_MS = 1000;
    static BUFFER_HEADROOM = 1.5;
    _inputBufferSize;
    _inputBuffer = null; // lazily allocated (needs data ctor)
    // Per-seq reckonTime stamp (server-clock ms), parallel to _inputBuffer — the
    // reconciler reads it back as ctx.reckonTime on the live step AND on replay, so
    // both hit-test at the exact instant the server rewinds to. Sized = replay ring.
    // Lazily allocated AND only when reckon stamping is on (`_stampReckon`): a room
    // without reckon lag-comp never rewinds to it, so we don't track it (reckonTimeAt
    // then reads 0 and the controller resolves ctx.reckonTime to serverNow()).
    _reckonTimes = null;
    // Reckon instant of the in-flight send() — computed in send() when stamping,
    // stamped on the wire AND recorded into _reckonTimes by _recordSent (same value
    // both). 0 when not stamping.
    _pendingReckon = 0;
    // Running baseline for the DELTA-CODED reliable stamp: the last timeline u32
    // sent. Each stamped send transmits `stamp − _lastStamp` (signed, ≈ one fixed
    // step per tick → ~1 byte vs a raw 4-byte u32); the server mirrors this baseline
    // and reliable+in-order keeps the two locked. reset() re-zeros it so the first
    // delta after a (re)connect carries the absolute, re-syncing with the server's
    // freshly-allocated baseline.
    _lastStamp = 0;
    // Per-seq timeline stamps for the UNRELIABLE ring, indexed `seq % historySize`
    // — exactly the slots a packet carries, so the index can't collide. Lazily
    // allocated, and only when the room asked for stamps. See _writeRingStamps.
    _stampRing = null;
    // Parallel to _stampRing, BOTH mode only: the renderDelta each slot was
    // sampled with, so the block can carry the exact value per slot.
    _renderDeltaRing = null;
    // Slots the encoder's ring currently carries (≤ historySize). Tracked, not
    // derived from `seq`: `reset()` drops the ring but keeps the seq monotonic.
    _ringSlots = 0;
    static _warnedBufferOverflow = false;
    static _warnedAllowRewindIgnored = false;
    // Dev diagnostic (see _warnUnknownFields): unknown data keys already warned.
    _warnedUnknownKeys = null;
    // Lag-comp stamp (server INPUT_OPTIONS handshake): which timeline(s) each
    // reliable input is prefixed with, DELTA-CODED on the wire (see _lastStamp).
    // Both → [varint Δreckon][u16 renderDelta]; reckon-only → [varint Δreckon];
    // render-only → [varint Δrender]; neither → no prefix.
    _stampRender = false;
    _stampReckon = false;
    // Optional per-send gate (app `allowRewind`): when set, only inputs it returns
    // true for carry the stamp — the rest skip it (and the TIMED bit), trimming the
    // timestamp on frames the server won't rewind (e.g. non-firing inputs). The
    // delta baseline (`_lastStamp`) only advances on stamped sends, so it stays
    // locked with the server across the gaps. Absent ⇒ stamp every reliable input.
    _allowRewind;
    // Send observers (the prediction layer subscribes here to step its simulation
    // on each send — see onSend). null until the first subscribe, so a handle used
    // without prediction pays nothing (no allocation, no per-send dispatch).
    // COPY-ON-WRITE: subscribe/unsubscribe replace the array (cold path), so the
    // dispatch loop's captured ref can't skip or double-fire when a listener
    // mutates the list mid-dispatch.
    _sendListeners = null;
    // The app's interpolation buffer (ms) — how far in the past it renders remote
    // entities (e.g. a `Predict` lerp `delay`). The stamp subtracts this AND the
    // one-way latency (smoothedRtt/2) the SDK already tracks, so callers pass only
    // the interp buffer, never the latency. When the app doesn't set it explicitly,
    // `predict.reconciler`/`sim` bind `_renderDelayProvider` to the Predict lerp
    // `delay` (see bindRenderDelay) so the interp buffer and the server's rewind
    // instant stay ONE value — no two-number "keep these equal" footgun.
    _renderDelay = 0;
    _renderDelayExplicit = false;
    _renderDelayProvider;
    // Server-advertised rates: fixed step (Hz), patch interval (ms = reconcile
    // cadence), and physics sub-steps per input tick.
    _tickRate;
    _patchRate;
    _subSteps = 1;
    constructor(host, data, encoder, opts) {
        this._host = host;
        this.data = data;
        this._encoder = encoder;
        this._stampRender = opts?.stampRender ?? false;
        this._stampReckon = opts?.stampReckon ?? false;
        this._allowRewind = opts?.allowRewind;
        // Inert on this channel, and silently so — the predicate is never even
        // called (see `send`). Say it once rather than let someone watch bandwidth
        // not move and go looking.
        if (this._allowRewind !== undefined && encoder.mode === "unreliable"
            && !InputHandleImpl._warnedAllowRewindIgnored) {
            InputHandleImpl._warnedAllowRewindIgnored = true;
            console.warn(`@colyseus/sdk: \`allowRewind\` is ignored on \`mode:"unreliable"\` — a packet ` +
                `stamps its whole redundancy ring or none of it, so excluding one input would ` +
                `cost bandwidth rather than save it. Use \`mode:"reliable"\` to gate the ` +
                `lag-comp stamp per input.`);
        }
        this._renderDelay = opts?.renderDelay ?? 0;
        this._renderDelayExplicit = opts?.renderDelay !== undefined;
        this._tickRate = opts?.tickRate;
        this._patchRate = opts?.patchRate;
        this._subSteps = opts?.subSteps ?? 1;
        // Size the replay ring to the advertised rates (see field comment).
        const stepMs = this._tickRate ? 1000 / this._tickRate : (1000 / 60);
        const window = InputHandleImpl.BUFFER_RTT_BUDGET_MS + (this._patchRate ?? 0);
        this._inputBufferSize = Math.max(InputHandleImpl.BUFFER_FLOOR, Math.ceil((window / stepMs) * InputHandleImpl.BUFFER_HEADROOM));
        this._sendTimes = new Float64Array(this._inputBufferSize);
    }
    get mode() { return this._encoder.mode; }
    get tickRate() { return this._tickRate; }
    // `1/hz` is correctly-rounded IEEE-754 → bit-identical to the server's stepSeconds.
    get stepSeconds() { return this._tickRate ? 1 / this._tickRate : undefined; }
    get stepMs() { return this._tickRate ? 1000 / this._tickRate : undefined; }
    get patchRate() { return this._patchRate; }
    get subSteps() { return this._subSteps; }
    // `(1/hz)/n` — the SAME expression the server's ctx.subDt uses → bit-identical dt.
    get subStepSeconds() { return this._tickRate ? (1 / this._tickRate) / this._subSteps : undefined; }
    get subStepMs() { return this._tickRate ? (1000 / this._tickRate) / this._subSteps : undefined; }
    get lastProcessed() { return this._lastProcessed; }
    get sentCount() { return this._sentCount; }
    get pendingCount() { return this._sentCount - this._lastProcessed; }
    get replayBufferSize() { return this._inputBufferSize; }
    get epoch() { return this._epoch; }
    at(seq) {
        if (this._inputBuffer === null)
            return undefined;
        // Buffered iff sent, not yet acked, and still within the bounded ring window.
        if (seq <= this._lastProcessed || seq > this._sentCount)
            return undefined;
        if (this._sentCount - seq >= this._inputBufferSize) {
            // Pending input aged out (RTT exceeded the buffer budget); reconcile may drift, so warn once.
            if (!InputHandleImpl._warnedBufferOverflow) {
                InputHandleImpl._warnedBufferOverflow = true;
                console.warn(`@colyseus/sdk: input replay buffer (${this._inputBufferSize}) overflowed — ` +
                    `RTT exceeds its budget at this input rate; reconciliation may drift.`);
            }
            return undefined;
        }
        return this._inputBuffer[seq % this._inputBufferSize];
    }
    reckonTimeAt(seq) {
        // 0 when reckon stamping is off (no ring) — the controller resolves to serverNow().
        if (this._reckonTimes === null)
            return 0;
        // Same validity window as at(): sent, unacked, still in the ring. Live reads
        // it for the just-sent seq (== sentCount); replay reads each unacked seq.
        if (seq <= this._lastProcessed || seq > this._sentCount)
            return 0;
        if (this._sentCount - seq >= this._inputBufferSize)
            return 0;
        return this._reckonTimes[seq % this._inputBufferSize];
    }
    reset() {
        this._encoder.reset();
        // Adopt the encoder's monotonic seq as the baseline: 0 for reliable, the current
        // framework seq for unreliable (the encoder keeps `_seq` across its reset). pending
        // starts at 0 so a reconnect doesn't replay already-acked inputs, and unreliable
        // seqs continue past the server's last-seen seq if the buffer was reused.
        this._sentCount = this._lastProcessed = this._encoder.seq;
        this._framed = null;
        this._lastStamp = 0; // next stamped send ships an absolute delta — re-syncs the server's re-zeroed baseline
        // The encoder dropped its ring but keeps `_seq`, so the slot count has to be
        // tracked rather than derived from the seq — else the next packet would
        // claim slots the ring no longer carries.
        this._ringSlots = 0;
        this._sendTimes.fill(0); // stale acks for pre-reset seqs must read as "unknown" (-1), not a bogus RTT
        // _inputBuffer is reused as-is: at() gates on _sentCount/_lastProcessed, so it can't surface stale snapshots.
        this._epoch++; // observing controllers poll this and follow the reset
    }
    /**
     * @internal Bind lag-comp's `renderDelay` to a live provider — the owning
     * Predict's lerp `delay`. Called by `predict.reconciler`/`predict.sim` when
     * they wire this handle, so the remote interp buffer and the server's rewind
     * instant are derived from ONE number and can't drift apart. No-op if the app
     * passed an explicit `renderDelay` to `room.input()` — an explicit value wins.
     */
    bindRenderDelay(provider) {
        if (this._renderDelayExplicit)
            return;
        this._renderDelayProvider = provider;
    }
    /** Effective interp buffer (ms): a bound provider (the Predict lerp `delay`)
     *  when present, else the static value from `room.input()`. */
    _resolveRenderDelay() {
        return this._renderDelayProvider ? this._renderDelayProvider() : this._renderDelay;
    }
    /** Dev diagnostic (debug overlay only). An assignment to an UNDECLARED field
     *  lands as a plain own property — declared fields live behind prototype
     *  setters in the dense `$values` array and never create own keys — so any
     *  own enumerable string key missing from the schema metadata is a write
     *  that will never be encoded. Warn once per key. */
    _warnUnknownFields() {
        const meta = metadataOf(this.data);
        if (!meta)
            return;
        for (const key of Object.keys(this.data)) {
            if (meta[key] !== undefined || this._warnedUnknownKeys?.has(key))
                continue;
            (this._warnedUnknownKeys ??= new Set()).add(key);
            console.warn(`@colyseus/sdk input: "${key}" is not a declared field on ` +
                `${this.data.constructor.name} — the write is never encoded or sent. ` +
                `Declare it with @type(...) on the input schema, or remove the write.`);
        }
    }
    send() {
        const conn = this._host.connection;
        if (!conn?.isOpen)
            return 0; // nothing transmitted → seq 0 (seqs are 1-based)
        if (debugOverlayActive())
            this._warnUnknownFields();
        // May be 0-length on a no-change delta tick → we still frame + send a
        // body-less input (server decodes it as a no-op, holding the last values).
        // Callers skip a tick by not calling send(), not by relying on suppression.
        const bytes = this._encoder.encode();
        const reliable = this._encoder.mode === "reliable";
        // Lag-comp stamp prefix. The two channels carry different shapes — one
        // delta-coded stamp vs a self-contained per-slot block; see
        // ProtocolModifier.TIMED and _writeRingStamps.
        const stampsEnabled = this._stampReckon || this._stampRender;
        // Evaluate `allowRewind` at most once: it's app code over live input data.
        // `allowRewind` gates the RELIABLE channel only — the unreliable ring is
        // all-or-nothing (see _writeRingStamps), so evaluating the app predicate
        // there would be misleading as well as wasted.
        const wantStamp = stampsEnabled && reliable
            && (this._allowRewind === undefined || this._allowRewind(this.data));
        const wantRingStamp = stampsEnabled && !reliable;
        const both = this._stampReckon && this._stampRender;
        const stampMax = wantStamp
            ? RELIABLE_STAMP_MAX + (both ? 2 : 0)
            : (wantRingStamp ? ringStampMax(this._encoder.historySize, both) : 0);
        const totalMax = 1 + stampMax + bytes.length;
        if (totalMax > this._scratch.byteLength) {
            this._scratch = new Uint8Array(Math.max(totalMax, this._scratch.byteLength * 2));
            this._framed = null; // cached view points into the old buffer
        }
        this._scratch[0] = (reliable ? Protocol.ROOM_INPUT_RELIABLE : Protocol.ROOM_INPUT_UNRELIABLE)
            | ((wantStamp || wantRingStamp) ? ProtocolModifier.TIMED : 0);
        const it = { offset: 1 };
        if (wantRingStamp) {
            this._writeRingStamps(it);
        }
        else if (wantStamp) {
            // Delta-coded against the running baseline; BOTH trails the absolute u16.
            const { stamp, renderDelta } = this._sampleStamp();
            encode.number(this._scratch, stamp - this._lastStamp, it);
            this._lastStamp = stamp;
            if (both) {
                encode.uint16(this._scratch, renderDelta, it);
            }
        }
        else {
            this._pendingReckon = 0; // not stamping → no reckon instant to record
        }
        // [stamp?][body] — body continues from the stamp's end offset.
        this._scratch.set(bytes, it.offset);
        const total = it.offset + bytes.length;
        if (this._framed === null || this._framed.byteLength !== total) {
            this._framed = this._scratch.subarray(0, total); // reused view (size varies only with the stamp prefix)
        }
        const framed = this._framed;
        let seq;
        if (reliable) {
            conn.send(framed);
            // Reliable: implicit count seq — the server counts received messages, so
            // `_sentCount` mirrors its consumed counter for the next TIMED ack.
            seq = ++this._sentCount;
        }
        else {
            conn.sendUnreliable(framed);
            // Unreliable: adopt the encoder's framework seq (stamped on the wire) so the
            // server's seq-value ack and this replay ring line up across packet loss.
            seq = this._sentCount = this._encoder.seq;
        }
        this._recordSent(seq);
        // Local, not _sentCount: a re-entrant send() from an onSend listener would
        // have advanced the field before this returns.
        return seq;
    }
    /**
     * @internal Write the unreliable channel's self-contained stamp block, then
     * leave `it` at the start of the ring body.
     *
     *     [varint k][uint32 newest][varint Δ]×(k−1)
     *     [uint16 rdNewest][varint Δrd]×(k−1)        ← BOTH mode only
     *
     * `k` is the slot count of the ring this packet carries (oldest→newest, the
     * order `InputDecoder.decodeAll` yields). `newest` is THIS send's timeline
     * instant, absolute — so a packet is readable on its own and no amount of
     * loss or reordering can desync a baseline. Each Δ walks one slot older
     * (`stamp[i] = stamp[i+1] − Δ`), which is ≈ one fixed step and so ~1 byte
     * through the self-describing number codec.
     *
     * BOTH mode appends the `renderDelta` series in the same shape, so every slot
     * carries the interp buffer + one-way latency it was actually sampled with,
     * rather than the newest slot's value smeared across the ring. Consecutive
     * values differ by ~0–1 ms (`renderDelay` is app-set and constant,
     * `smoothedRtt` is smoothed), which lands in the codec's 1-byte fixnum range —
     * so exactness costs one byte per redundant slot, and a violent RTT swing
     * degrades to at most 3 (the u16 range), never more.
     *
     * All-or-nothing: every slot in the block is stamped, or the room asked for
     * no stamps and there is no block. `allowRewind` does not apply here — the
     * block ships whole, so excluding one slot would save nothing while making
     * its neighbour's delta swing the full absolute value. The one transient
     * exception is the pre-clock-sync window, where the slots genuinely have no
     * known instant and ship as 0 for the server to read live.
     */
    _writeRingStamps(it) {
        const { stamp, renderDelta } = this._sampleStamp();
        const historySize = this._encoder.historySize;
        const seq = this._encoder.seq; // this send's seq — already advanced by encode()
        // The packet carries the last `historySize` sends, or fewer while the ring
        // is still filling (from construction or a reset()).
        const k = this._ringSlots = Math.min(this._ringSlots + 1, historySize);
        const ring = (this._stampRing ??= new Float64Array(historySize));
        ring[seq % historySize] = stamp;
        encode.number(this._scratch, k, it);
        encode.uint32(this._scratch, stamp, it);
        this._writeSeriesDeltas(ring, seq, k, it);
        if (this._stampReckon && this._stampRender) {
            // Uint16Array: renderDelta is already clamped to the u16 range, so the
            // ring stores it exactly at half the width of the timeline ring.
            const rdRing = (this._renderDeltaRing ??= new Uint16Array(historySize));
            rdRing[seq % historySize] = renderDelta;
            encode.uint16(this._scratch, renderDelta, it);
            this._writeSeriesDeltas(rdRing, seq, k, it);
        }
    }
    /** Walk `k` ring slots newest→oldest, emitting each step as a signed delta. */
    _writeSeriesDeltas(ring, seq, k, it) {
        const size = ring.length;
        for (let i = 1; i < k; i++) {
            encode.number(this._scratch, ring[(seq - i + 1) % size] - ring[(seq - i) % size], it);
        }
    }
    /**
     * @internal Sample this send's lag-comp instants from the clock, and record
     * the reckon one for {@link reckonTimeAt}.
     *
     * `renderDelta` is the interp buffer (`renderDelay`, app-set) plus the one-way
     * downstream latency (≈ `smoothedRtt/2`, ours). `stamp` is the timeline this
     * room actually rewinds on: reckon rooms ship the estimate directly — the
     * server reads its history at that index, so clock/RTT estimation error
     * cancels (client displayed f(est), server reads f(est)) — while snapshot
     * rooms ship `reckonTime − renderDelta`, the instant lerped remotes were on
     * screen. BOTH ships reckon plus the gap and lets the server subtract.
     *
     * Everything is 0 before the clock syncs, or when `allowRewind` excluded this
     * input: the server then falls back to live positions rather than trusting a
     * bogus instant.
     */
    _sampleStamp() {
        const clock = this._host.clock;
        const synced = (clock?.lastServerTime?.() ?? 0) > 0;
        const rk = synced ? Math.max(0, Math.round(clock.serverNow())) >>> 0 : 0;
        const renderDelta = synced
            ? Math.min(0xffff, Math.max(0, Math.round(this._resolveRenderDelay() + (clock.smoothedRtt?.() ?? 0) / 2)))
            : 0;
        this._pendingReckon = rk;
        // rk is 0 while unsynced, so this floors to 0 on its own.
        return { stamp: this._stampReckon ? rk : (rk > renderDelta ? rk - renderDelta : 0), renderDelta };
    }
    /**
     * @internal Snapshot the just-sent input into the replay ring and stamp its
     * send time, keyed by `seq`. Lets a reconciler replay unacked inputs via
     * {@link at} and the TIMED ack sample RTT. The snapshot is alloc-free through
     * the codec's `copyInto` (no `Object.keys`), which stages every field, so
     * each slot is a full snapshot independent of the wire delta encoding.
     */
    _recordSent(seq) {
        if (this._inputBuffer === null) {
            const Ctor = this.data.constructor;
            this._inputBuffer = Array.from({ length: this._inputBufferSize }, () => new Ctor());
        }
        this._encoder.copyInto(this._inputBuffer[seq % this._inputBufferSize]);
        this._sendTimes[seq % this._inputBufferSize] = now();
        // Reckon ring only when reckon stamping is on (see _reckonTimes) — a room
        // without reckon lag-comp never rewinds to it, so the allocation is skipped.
        if (this._stampReckon) {
            (this._reckonTimes ??= new Float64Array(this._inputBufferSize))[seq % this._inputBufferSize] = this._pendingReckon;
        }
        // Notify send observers LAST — the replay ring + reckon instant are now
        // recorded, so a listener can read `at(seq)` / `reckonTimeAt(seq)`.
        const ls = this._sendListeners;
        if (ls !== null)
            for (let i = 0; i < ls.length; i++)
                ls[i](seq);
    }
    onSend(listener) {
        const ls = this._sendListeners;
        this._sendListeners = ls !== null ? [...ls, listener] : [listener];
        return () => {
            const cur = this._sendListeners;
            if (cur === null)
                return;
            const i = cur.indexOf(listener);
            if (i < 0)
                return;
            const next = cur.slice();
            next.splice(i, 1);
            this._sendListeners = next.length > 0 ? next : null;
        };
    }
    /**
     * @internal Feed the server's last-PROCESSED input seq (decoded from the
     * TIMED prefix). Advances {@link lastProcessed} (monotonic) and returns the
     * round-trip time sample for that ack (`now − sendTime(seq)`), or `-1` if the
     * send time is unknown. The {@link RoomClockImpl} filters/EMA-smooths the sample.
     */
    ackInput(seq) {
        if (seq <= this._lastProcessed)
            return -1;
        // Aged out of the seq ring window → RTT unknown.
        const aged = this._sentCount - seq >= this._inputBufferSize;
        this._lastProcessed = seq;
        if (aged)
            return -1;
        const sentAt = this._sendTimes[seq % this._inputBufferSize];
        return sentAt > 0 ? now() - sentAt : -1;
    }
}

export { InputHandleImpl };
//# sourceMappingURL=InputHandle.mjs.map
